/**
 * General Ledger module mock data — accounts, journal entries, lines, and
 * computed-on-the-fly report builders.
 *
 * All amounts are BigInt cents in BDT base. The data is hand-balanced:
 * every entry's debits and credits sum to the same value. Helpers like
 * `trialBalance`, `incomeStatement`, `balanceSheet`, and `arAging` work
 * directly on these structures so the UI is browseable end-to-end without
 * a backend.
 *
 * NOTE: Mirrors the shape of the real InstantDB queries on the read side so
 * routes can swap mock ↔ live with minimal code changes.
 */

import { emptyIfInstant } from "./mockMode";

const NOW = new Date("2026-05-09T08:00:00Z").getTime();

/* ─────────────────────────────────────────────────────────────
 * Account types
 * ───────────────────────────────────────────────────────────── */

export type MockAccountType =
  | "asset"
  | "liability"
  | "equity"
  | "revenue"
  | "expense";

export type MockNormalBalance = "debit" | "credit";

export type MockAccount = {
  _id: string;
  code: string;
  name: string;
  accountType: MockAccountType;
  parentAccountId: string | null;
  currency: string;
  isActive: boolean;
  description: string | null;
  normalBalance: MockNormalBalance;
  /** Pre-computed current balance in base cents (signed for natural side). */
  currentBalanceInBaseCents: bigint;
};

export const mockAccounts: MockAccount[] = emptyIfInstant<MockAccount>([]);

/* ─────────────────────────────────────────────────────────────
 * Journal entries + lines
 *
 * 30 entries: 5 draft, 22 posted, 3 reversed. Dates spread across the last
 * 90 days. Lines are perfectly balanced per entry.
 * ───────────────────────────────────────────────────────────── */

export type MockJournalStatus = "draft" | "posted" | "reversed";

export type MockJournalEntry = {
  _id: string;
  entryNumber: string; // empty for drafts
  entryDate: number;
  description: string;
  sourceTable: string | null;
  sourceId: string | null;
  status: MockJournalStatus;
  postedAt: number | null;
  reversedByEntryId: string | null;
  reversalReason: string | null;
};

export type MockJournalLine = {
  _id: string;
  journalEntryId: string;
  entryDate: number;
  accountId: string;
  debitInBaseCents: bigint;
  creditInBaseCents: bigint;
  currency: string;
  originalAmountCents: bigint;
  fxRateToBaseE8: bigint;
  description: string | null;
  sortOrder: number;
};

export const mockJournalEntries: MockJournalEntry[] = emptyIfInstant<MockJournalEntry>([]);

const IDENTITY_RATE = 100_000_000n;

let lineCounter = 0;
function L(
  entryId: string,
  accountId: string,
  debit: bigint,
  credit: bigint,
  description: string,
  entryDate: number,
  sortOrder: number,
): MockJournalLine {
  lineCounter += 1;
  return {
    _id: `jl_${String(lineCounter).padStart(4, "0")}`,
    journalEntryId: entryId,
    entryDate,
    accountId,
    debitInBaseCents: debit,
    creditInBaseCents: credit,
    currency: "BDT",
    originalAmountCents: debit > 0n ? debit : credit,
    fxRateToBaseE8: IDENTITY_RATE,
    description,
    sortOrder,
  };
}

/**
 * Hand-built balanced lines per entry. Numbers are realistic but tuned so
 * report subtotals look sensible.
 */
export const mockJournalLines: MockJournalLine[] = emptyIfInstant<MockJournalLine>([]);

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

export function findAccount(id: string): MockAccount | undefined {
  return mockAccounts.find((a) => a._id === id);
}

export function findAccountByCode(code: string): MockAccount | undefined {
  return mockAccounts.find((a) => a.code === code);
}

export function findEntry(id: string): MockJournalEntry | undefined {
  return mockJournalEntries.find((e) => e._id === id);
}

export function linesForEntry(entryId: string): MockJournalLine[] {
  return mockJournalLines
    .filter((l) => l.journalEntryId === entryId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function entryTotals(entryId: string): {
  debits: bigint;
  credits: bigint;
  isBalanced: boolean;
} {
  const lines = linesForEntry(entryId);
  let debits = 0n;
  let credits = 0n;
  for (const l of lines) {
    debits += l.debitInBaseCents;
    credits += l.creditInBaseCents;
  }
  return { debits, credits, isBalanced: debits === credits };
}

export function linesForAccount(
  accountId: string,
  range?: { fromDate?: number; toDate?: number },
): Array<MockJournalLine & { entry: MockJournalEntry }> {
  const fromDate = range?.fromDate ?? 0;
  const toDate = range?.toDate ?? Number.MAX_SAFE_INTEGER;
  const out: Array<MockJournalLine & { entry: MockJournalEntry }> = [];
  for (const l of mockJournalLines) {
    if (l.accountId !== accountId) continue;
    if (l.entryDate < fromDate || l.entryDate > toDate) continue;
    const entry = findEntry(l.journalEntryId);
    if (!entry || entry.status !== "posted") continue;
    out.push({ ...l, entry });
  }
  out.sort((a, b) => a.entryDate - b.entryDate);
  return out;
}

/**
 * Account balance from posted lines as of a date. Asset/Expense → debit-positive;
 * Liability/Equity/Revenue → credit-positive.
 */
export function accountBalance(accountId: string, asOfDate: number): bigint {
  const acc = findAccount(accountId);
  if (!acc) return 0n;
  const debitNormal = acc.normalBalance === "debit";
  let net = 0n;
  for (const l of mockJournalLines) {
    if (l.accountId !== accountId) continue;
    if (l.entryDate > asOfDate) continue;
    const entry = findEntry(l.journalEntryId);
    if (!entry || entry.status !== "posted") continue;
    net += l.debitInBaseCents - l.creditInBaseCents;
  }
  return debitNormal ? net : -net;
}

/* ─────────────────────────────────────────────────────────────
 * Report builders (mirror InstantDB shapes)
 * ───────────────────────────────────────────────────────────── */

export type TrialBalanceRow = {
  accountId: string;
  code: string;
  name: string;
  accountType: MockAccountType;
  debitsInBaseCents: bigint;
  creditsInBaseCents: bigint;
  naturalSide: MockNormalBalance;
  /**
   * True when this row represents the Opening Balance Equity account (3999).
   * The UI uses this to render a subtle "Opening" note in the description column.
   */
  isOpeningBalance?: boolean;
};

export function trialBalance(asOfDate: number): {
  asOfDate: number;
  rows: TrialBalanceRow[];
  totals: { debitsInBaseCents: bigint; creditsInBaseCents: bigint; isBalanced: boolean };
} {
  const obeAccountId = mockOpeningBalanceConfig.obeAccountId;
  const rows: TrialBalanceRow[] = [];
  let totalDebits = 0n;
  let totalCredits = 0n;
  for (const acc of mockAccounts) {
    if (acc._id === "acc_1500") continue; // skip pure-parent placeholder
    const balance = accountBalance(acc._id, asOfDate);
    const debitNormal = acc.normalBalance === "debit";
    const debitColumn =
      debitNormal && balance >= 0n
        ? balance
        : !debitNormal && balance < 0n
          ? -balance
          : 0n;
    const creditColumn =
      !debitNormal && balance >= 0n
        ? balance
        : debitNormal && balance < 0n
          ? -balance
          : 0n;
    rows.push({
      accountId: acc._id,
      code: acc.code,
      name: acc.name,
      accountType: acc.accountType,
      debitsInBaseCents: debitColumn,
      creditsInBaseCents: creditColumn,
      naturalSide: acc.normalBalance,
      // Flag the Opening Balance Equity account so the UI can annotate it.
      isOpeningBalance: acc._id === obeAccountId ? true : undefined,
    });
    totalDebits += debitColumn;
    totalCredits += creditColumn;
  }
  rows.sort((a, b) => a.code.localeCompare(b.code));
  return {
    asOfDate,
    rows,
    totals: {
      debitsInBaseCents: totalDebits,
      creditsInBaseCents: totalCredits,
      isBalanced: totalDebits === totalCredits,
    },
  };
}

export type IncomeStatementShape = {
  fromDate: number;
  toDate: number;
  revenue: { accounts: AccountReportLine[]; totalInBaseCents: bigint };
  costOfGoodsSold: { accounts: AccountReportLine[]; totalInBaseCents: bigint };
  grossProfitInBaseCents: bigint;
  operatingExpenses: { accounts: AccountReportLine[]; totalInBaseCents: bigint };
  netIncomeInBaseCents: bigint;
};

export type AccountReportLine = {
  accountId: string;
  code: string;
  name: string;
  amountInBaseCents: bigint;
};

function balanceWithinWindow(
  accountId: string,
  fromDate: number,
  toDate: number,
): bigint {
  const acc = findAccount(accountId);
  if (!acc) return 0n;
  const debitNormal = acc.normalBalance === "debit";
  let net = 0n;
  for (const l of mockJournalLines) {
    if (l.accountId !== accountId) continue;
    if (l.entryDate < fromDate || l.entryDate > toDate) continue;
    const entry = findEntry(l.journalEntryId);
    if (!entry || entry.status !== "posted") continue;
    net += l.debitInBaseCents - l.creditInBaseCents;
  }
  return debitNormal ? net : -net;
}

export function incomeStatement(fromDate: number, toDate: number): IncomeStatementShape {
  const revenue: AccountReportLine[] = [];
  const cogs: AccountReportLine[] = [];
  const opex: AccountReportLine[] = [];
  for (const acc of mockAccounts) {
    // Only revenue and expense accounts belong in P&L. Asset, liability, and
    // equity accounts — including 3999 Opening Balance Equity — are
    // balance-sheet items and must never appear here.
    if (acc.accountType !== "revenue" && acc.accountType !== "expense") continue;
    const amt = balanceWithinWindow(acc._id, fromDate, toDate);
    if (amt === 0n) continue;
    const line: AccountReportLine = {
      accountId: acc._id,
      code: acc.code,
      name: acc.name,
      amountInBaseCents: amt,
    };
    if (acc.accountType === "revenue") revenue.push(line);
    else if (acc.code === "5000") cogs.push(line);
    else opex.push(line);
  }
  revenue.sort((a, b) => a.code.localeCompare(b.code));
  cogs.sort((a, b) => a.code.localeCompare(b.code));
  opex.sort((a, b) => a.code.localeCompare(b.code));
  const totalRevenue = revenue.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalCogs = cogs.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalOpex = opex.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const grossProfit = totalRevenue - totalCogs;
  return {
    fromDate,
    toDate,
    revenue: { accounts: revenue, totalInBaseCents: totalRevenue },
    costOfGoodsSold: { accounts: cogs, totalInBaseCents: totalCogs },
    grossProfitInBaseCents: grossProfit,
    operatingExpenses: { accounts: opex, totalInBaseCents: totalOpex },
    netIncomeInBaseCents: grossProfit - totalOpex,
  };
}

export type BalanceSheetShape = {
  asOfDate: number;
  assets: { accounts: AccountReportLine[]; totalInBaseCents: bigint };
  liabilities: { accounts: AccountReportLine[]; totalInBaseCents: bigint };
  equity: { accounts: AccountReportLine[]; totalInBaseCents: bigint };
  verification: {
    liabilitiesPlusEquityInBaseCents: bigint;
    deltaInBaseCents: bigint;
    isBalanced: boolean;
  };
};

export function balanceSheet(asOfDate: number): BalanceSheetShape {
  const assets: AccountReportLine[] = [];
  const liabilities: AccountReportLine[] = [];
  const equity: AccountReportLine[] = [];

  // Use the pre-tuned `currentBalanceInBaseCents` so the balance sheet is
  // realistic and self-consistent regardless of which subset of journal
  // entries has been posted in the mock data.
  for (const acc of mockAccounts) {
    if (acc._id === "acc_1500") continue; // skip parent
    const amt = acc.currentBalanceInBaseCents;
    if (amt === 0n && acc.accountType !== "equity") continue;
    const line: AccountReportLine = {
      accountId: acc._id,
      code: acc.code,
      name: acc.name,
      amountInBaseCents: amt,
    };
    if (acc.accountType === "asset") assets.push(line);
    else if (acc.accountType === "liability") liabilities.push(line);
    else if (acc.accountType === "equity") equity.push(line);
  }

  // Compute current-year earnings from the pre-tuned snapshot balances so
  // assets always equal liabilities + equity in the mock data. The income
  // statement (which derives from journal lines) keeps its own numbers.
  let revenueTotal = 0n;
  let expenseTotal = 0n;
  for (const acc of mockAccounts) {
    if (acc.accountType === "revenue") {
      revenueTotal += acc.currentBalanceInBaseCents;
    } else if (acc.accountType === "expense") {
      expenseTotal += acc.currentBalanceInBaseCents;
    }
  }
  const earnings = revenueTotal - expenseTotal;
  if (earnings !== 0n) {
    equity.push({
      accountId: "current_earnings",
      code: "—",
      name: "Current Earnings",
      amountInBaseCents: earnings,
    });
  }

  assets.sort((a, b) => a.code.localeCompare(b.code));
  liabilities.sort((a, b) => a.code.localeCompare(b.code));
  equity.sort((a, b) => a.code.localeCompare(b.code));

  const totalAssets = assets.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const totalLiabilities = liabilities.reduce(
    (s, r) => s + r.amountInBaseCents,
    0n,
  );
  const totalEquity = equity.reduce((s, r) => s + r.amountInBaseCents, 0n);
  const liabilitiesPlusEquity = totalLiabilities + totalEquity;
  return {
    asOfDate,
    assets: { accounts: assets, totalInBaseCents: totalAssets },
    liabilities: { accounts: liabilities, totalInBaseCents: totalLiabilities },
    equity: { accounts: equity, totalInBaseCents: totalEquity },
    verification: {
      liabilitiesPlusEquityInBaseCents: liabilitiesPlusEquity,
      deltaInBaseCents: totalAssets - liabilitiesPlusEquity,
      isBalanced: totalAssets === liabilitiesPlusEquity,
    },
  };
}

/* ─────────────────────────────────────────────────────────────
 * AR Aging — derived from a small set of mock invoices keyed by dealer.
 *
 * In addition to invoices, dealer opening balances (`dealer.openingBalanceBaseCents`
 * dated at `dealer.openingBalanceAsOf`) are folded in as "phantom invoices" —
 * the standard ERP pattern for ageing carry-forward AR. Each opening line:
 *   - is bucketed by `(asOfDate − openingBalanceAsOf)` like a real invoice,
 *   - is labelled "Opening balance" so the row breakdown distinguishes it.
 * ───────────────────────────────────────────────────────────── */

export type ArAgingBucket = "0-30" | "31-60" | "61-90" | "90+";

/**
 * One contributing item to a dealer's aged AR — either a real invoice or an
 * opening-balance carry-forward. The UI uses `kind` to render an "Opening"
 * tag inline with the dealer name when expanded.
 */
export type ArAgingItem = {
  kind: "invoice" | "opening";
  /** Display label: invoice number, or "Opening balance" for carry-forwards. */
  label: string;
  balanceInBaseCents: bigint;
  ageDays: number;
  bucket: ArAgingBucket;
};

export type ArAgingRow = {
  dealerId: string;
  dealerName: string;
  totalInBaseCents: bigint;
  buckets: Record<ArAgingBucket, bigint>;
  /** Per-row contribution breakdown — invoices + opening-balance phantoms. */
  items: ArAgingItem[];
  /** True if any of the contributing items came from the opening balance. */
  hasOpeningBalance: boolean;
};

const MOCK_OPEN_INVOICES: Array<{
  dealerId: string;
  dealerName: string;
  invoiceNumber: string;
  balanceInBaseCents: bigint;
  ageDays: number;
}> = [
  { dealerId: "dlr_001", dealerName: "Bashundhara Mart", invoiceNumber: "INV-2026-000041", balanceInBaseCents: 1_858_000_00n, ageDays: 12 },
  { dealerId: "dlr_001", dealerName: "Bashundhara Mart", invoiceNumber: "INV-2026-000038", balanceInBaseCents: 540_000_00n, ageDays: 38 },
  { dealerId: "dlr_002", dealerName: "Karim Distributors", invoiceNumber: "INV-2026-000044", balanceInBaseCents: 1_240_000_00n, ageDays: 26 },
  { dealerId: "dlr_002", dealerName: "Karim Distributors", invoiceNumber: "INV-2026-000031", balanceInBaseCents: 415_000_00n, ageDays: 71 },
  { dealerId: "dlr_003", dealerName: "Hossain Stores", invoiceNumber: "INV-2026-000045", balanceInBaseCents: 720_000_00n, ageDays: 18 },
  { dealerId: "dlr_004", dealerName: "Sylhet Trading Co.", invoiceNumber: "INV-2026-000035", balanceInBaseCents: 920_000_00n, ageDays: 55 },
  { dealerId: "dlr_005", dealerName: "Chittagong Wholesale", invoiceNumber: "INV-2026-000022", balanceInBaseCents: 380_000_00n, ageDays: 102 },
  { dealerId: "dlr_005", dealerName: "Chittagong Wholesale", invoiceNumber: "INV-2026-000046", balanceInBaseCents: 1_180_000_00n, ageDays: 4 },
  { dealerId: "dlr_006", dealerName: "Rajshahi Mart Ltd.", invoiceNumber: "INV-2026-000028", balanceInBaseCents: 285_000_00n, ageDays: 88 },
  { dealerId: "dlr_007", dealerName: "Khulna Dealer Hub", invoiceNumber: "INV-2026-000043", balanceInBaseCents: 1_212_000_00n, ageDays: 33 },
];

/**
 * Carry-forward dealer openings — paired with the `dealers.openingBalanceBaseCents`
 * fields in `salesMock.ts`. Kept inline here (rather than imported) to avoid a
 * circular module dependency between `glMock` and `salesMock`.
 *
 * Mirrors the live backend: AR Aging's query reads each dealer row and
 * synthesises a phantom invoice dated at `openingBalanceAsOf`.
 */
const MOCK_OPENING_AR_BALANCES: Array<{
  dealerId: string;
  dealerName: string;
  balanceInBaseCents: bigint;
  asOfDate: number;
}> = [
  {
    dealerId: "dealer-001",
    dealerName: "Karim Trading Co.",
    balanceInBaseCents: 1_250_000_00n,
    asOfDate: Date.UTC(2026, 0, 1),
  },
  {
    dealerId: "dealer-002",
    dealerName: "Rahman Distributors",
    balanceInBaseCents: 4_230_000_00n,
    asOfDate: Date.UTC(2026, 0, 1),
  },
  {
    dealerId: "dealer-003",
    dealerName: "Bengal Foods Ltd.",
    balanceInBaseCents: 420_000_00n,
    asOfDate: Date.UTC(2026, 0, 1),
  },
];

function bucketForAgeDays(ageDays: number): ArAgingBucket {
  return ageDays <= 30
    ? "0-30"
    : ageDays <= 60
      ? "31-60"
      : ageDays <= 90
        ? "61-90"
        : "90+";
}

export function arAging(asOfDate: number): {
  asOfDate: number;
  rows: ArAgingRow[];
  bucketTotals: Record<ArAgingBucket, bigint>;
  grandTotalInBaseCents: bigint;
} {
  const map = new Map<string, ArAgingRow>();

  function ensureRow(dealerId: string, dealerName: string): ArAgingRow {
    let row = map.get(dealerId);
    if (!row) {
      row = {
        dealerId,
        dealerName,
        totalInBaseCents: 0n,
        buckets: { "0-30": 0n, "31-60": 0n, "61-90": 0n, "90+": 0n },
        items: [],
        hasOpeningBalance: false,
      };
      map.set(dealerId, row);
    }
    return row;
  }

  for (const inv of MOCK_OPEN_INVOICES) {
    const bucket = bucketForAgeDays(inv.ageDays);
    const row = ensureRow(inv.dealerId, inv.dealerName);
    row.totalInBaseCents += inv.balanceInBaseCents;
    row.buckets[bucket] += inv.balanceInBaseCents;
    row.items.push({
      kind: "invoice",
      label: inv.invoiceNumber,
      balanceInBaseCents: inv.balanceInBaseCents,
      ageDays: inv.ageDays,
      bucket,
    });
  }

  // Fold opening AR balances in as phantom invoices dated at `asOfDate`.
  // Each opening contributes one item bucketed by (asOfDate − openingAsOf).
  const DAY = 24 * 60 * 60 * 1000;
  for (const op of MOCK_OPENING_AR_BALANCES) {
    if (op.balanceInBaseCents === 0n) continue;
    if (op.asOfDate > asOfDate) continue; // opening dated after report date
    const ageDays = Math.max(0, Math.floor((asOfDate - op.asOfDate) / DAY));
    const bucket = bucketForAgeDays(ageDays);
    const row = ensureRow(op.dealerId, op.dealerName);
    row.totalInBaseCents += op.balanceInBaseCents;
    row.buckets[bucket] += op.balanceInBaseCents;
    row.hasOpeningBalance = true;
    row.items.push({
      kind: "opening",
      label: "Opening balance",
      balanceInBaseCents: op.balanceInBaseCents,
      ageDays,
      bucket,
    });
  }

  const rows = Array.from(map.values()).sort((a, b) =>
    Number(b.totalInBaseCents - a.totalInBaseCents),
  );
  const bucketTotals: Record<ArAgingBucket, bigint> = {
    "0-30": 0n,
    "31-60": 0n,
    "61-90": 0n,
    "90+": 0n,
  };
  let grand = 0n;
  for (const r of rows) {
    grand += r.totalInBaseCents;
    for (const k of Object.keys(bucketTotals) as ArAgingBucket[]) {
      bucketTotals[k] += r.buckets[k];
    }
  }
  return { asOfDate, rows, bucketTotals, grandTotalInBaseCents: grand };
}

/* ─────────────────────────────────────────────────────────────
 * Period helpers used by the route components
 * ───────────────────────────────────────────────────────────── */

export type PeriodKey = "month" | "quarter" | "fiscal_year" | "custom";

/** Default fiscal year is Jul→Jun (Bangladesh). */
export function getFiscalYearWindow(asOf: number = NOW): {
  fromDate: number;
  toDate: number;
} {
  const d = new Date(asOf);
  const m = d.getUTCMonth(); // 0-indexed
  const y = d.getUTCFullYear();
  const fyStartYear = m >= 6 ? y : y - 1; // months 6..11 → current year start
  return {
    fromDate: Date.UTC(fyStartYear, 6, 1),
    toDate: asOf,
  };
}

export function getCurrentMonthWindow(asOf: number = NOW): {
  fromDate: number;
  toDate: number;
} {
  const d = new Date(asOf);
  return {
    fromDate: Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1),
    toDate: asOf,
  };
}

export function getCurrentQuarterWindow(asOf: number = NOW): {
  fromDate: number;
  toDate: number;
} {
  const d = new Date(asOf);
  const q = Math.floor(d.getUTCMonth() / 3) * 3;
  return {
    fromDate: Date.UTC(d.getUTCFullYear(), q, 1),
    toDate: asOf,
  };
}

/** Convenience export. Date the mock dataset is "as of". */
export const MOCK_NOW = NOW;

/* ─────────────────────────────────────────────────────────────
 * Opening Balance — singleton config + a small pre-seeded set so the demo
 * shows the workflow without needing the user to type anything.
 *
 * Status: "draft" — the master entry is still being built. Once the user
 * clicks "Close Opening Balance" on the UI, the singleton flips to "closed"
 * and the journal entry posts as `OPENING-{asOfDate}`.
 * ───────────────────────────────────────────────────────────── */

export type MockOpeningBalanceConfig = {
  _id: string;
  key: string;
  asOfDate: number;
  obeAccountId: string;
  openingJournalEntryId: string | null;
  status: "draft" | "posted" | "closed";
  closedAt: number | null;
};

/** Default singleton. As-of = Jan 1 2026 to match the calendar-year fiscal demo. */
export const mockOpeningBalanceConfig: MockOpeningBalanceConfig = {
  _id: "obc_default",
  key: "default",
  asOfDate: Date.UTC(2026, 0, 1),
  obeAccountId: "acc_3999",
  openingJournalEntryId: "je_OPENING_2026",
  status: "draft",
  closedAt: null,
};

/**
 * A categorised view of opening lines so UI list components can render the
 * five tabs (dealers / suppliers / loans / employees / accounts) without
 * filtering through journal lines.
 *
 * Each row carries the dealer/supplier id (where applicable) plus the signed
 * amount in BDT base cents.
 */
export type MockOpeningEntryRow = {
  /** Line id placeholder — stable across reloads of the mock data. */
  lineId: string;
  /** Source category for the UI tab routing. */
  category: "dealer" | "supplier" | "loan" | "employee" | "account" | "inventory";
  /** Free-form display label (e.g. "Karim Trading Co.", "Bank — DBBL"). */
  label: string;
  /** Optional FK to the source row (dealerId / supplierId / loanId). */
  sourceId: string | null;
  /** GL account this opening line targets. */
  accountId: string;
  accountCode: string;
  /** Signed: +ve = debit on `accountId`; −ve = credit. */
  signedBaseCents: bigint;
};

/**
 * Pre-seeded opening rows. Three dealers carry forward a non-zero AR balance
 * so the demo's AR Aging widget already has something to show; the totals
 * are debit on AR and credit on OBE.
 */
export const mockOpeningEntries: MockOpeningEntryRow[] = emptyIfInstant<MockOpeningEntryRow>([]);

/** Aggregated debit / credit totals for the master opening entry. */
export function mockOpeningEntryTotals(): {
  debits: bigint;
  credits: bigint;
  isBalanced: boolean;
} {
  let nonObeDebits = 0n;
  let nonObeCredits = 0n;
  for (const row of mockOpeningEntries) {
    if (row.signedBaseCents > 0n) nonObeDebits += row.signedBaseCents;
    else nonObeCredits += -row.signedBaseCents;
  }
  // OBE absorbs the residual to keep the entry balanced.
  const obeCredit = nonObeDebits > nonObeCredits ? nonObeDebits - nonObeCredits : 0n;
  const obeDebit = nonObeCredits > nonObeDebits ? nonObeCredits - nonObeDebits : 0n;
  return {
    debits: nonObeDebits + obeDebit,
    credits: nonObeCredits + obeCredit,
    isBalanced: nonObeDebits + obeDebit === nonObeCredits + obeCredit,
  };
}

/* ─────────────────────────────────────────────────────────────
 * Splice the master opening entry into mockJournalEntries / mockJournalLines.
 *
 * The real backend posts a single master journal entry
 * (`OPENING-{asOfDate}`) with one debit/credit pair per opening source plus a
 * balancing OBE leg. Reports that aggregate posted journal lines therefore
 * pick the openings up "for free".
 *
 * We mirror that here so the Trial Balance, Balance Sheet, GL view, and
 * Account Statement mocks behave identically to the live backend.
 *
 * The singleton's `status` controls visibility:
 *   - `"draft"` → master entry rendered as a draft (KPI count, GL list under
 *     the Drafts tab). Reports filtered to posted entries skip it.
 *   - `"posted"` / `"closed"` → master entry rendered as posted. Trial
 *     Balance, Balance Sheet, etc. include its lines.
 *
 * In v1 we deliberately default the demo singleton to `"posted"` so the
 * pre-seeded openings show up in every report on first paint — the demo is
 * meant to look "live", not perpetually in draft.
 * ───────────────────────────────────────────────────────────── */
{
  const obeAccountId = mockOpeningBalanceConfig.obeAccountId;
  const asOfDate = mockOpeningBalanceConfig.asOfDate;
  const masterEntryId = mockOpeningBalanceConfig.openingJournalEntryId ?? "je_OPENING_2026";
  // Keep the demo in "draft" so the opening-balances workflow page is
  // editable on first paint. Downstream reports that rely on posted journal
  // entries still pick up the seeded master entry because its journal-entry
  // status is independently "posted" (see `masterEntry` below).
  // The singleton config status only gates the UI-level editability on the
  // opening-balances settings page.

  const dateLabel = new Date(asOfDate).toISOString().slice(0, 10);
  const masterEntry: MockJournalEntry = {
    _id: masterEntryId,
    entryNumber: `OPENING-${dateLabel}`,
    entryDate: asOfDate,
    description: `Opening balances as of ${dateLabel}`,
    sourceTable: "openingBalanceConfig",
    sourceId: mockOpeningBalanceConfig._id,
    status: "posted",
    postedAt: asOfDate,
    reversedByEntryId: null,
    reversalReason: null,
  };
  mockJournalEntries.push(masterEntry);

  // One target-account leg per source row plus a single balancing OBE leg.
  let nonObeDebits = 0n;
  let nonObeCredits = 0n;
  let sortIdx = 0;
  for (const row of mockOpeningEntries) {
    const debit = row.signedBaseCents > 0n ? row.signedBaseCents : 0n;
    const credit = row.signedBaseCents < 0n ? -row.signedBaseCents : 0n;
    nonObeDebits += debit;
    nonObeCredits += credit;
    mockJournalLines.push(
      L(
        masterEntryId,
        row.accountId,
        debit,
        credit,
        // Description prefix matches what `finance/opening.ts` writes so the
        // categorised query lines look identical between mock and live data.
        row.category === "dealer"
          ? `Opening AR — ${row.label}`
          : row.category === "supplier"
            ? `Opening AP — ${row.label}`
            : row.category === "loan"
              ? `Opening loan — ${row.label}`
              : row.category === "employee"
                ? `Opening employee advance — ${row.label}`
                : `Opening balance — ${row.label}`,
        asOfDate,
        sortIdx++,
      ),
    );
  }
  // OBE balancing leg.
  const obeDebit = nonObeCredits > nonObeDebits ? nonObeCredits - nonObeDebits : 0n;
  const obeCredit = nonObeDebits > nonObeCredits ? nonObeDebits - nonObeCredits : 0n;
  if (obeDebit > 0n || obeCredit > 0n) {
    mockJournalLines.push(
      L(
        masterEntryId,
        obeAccountId,
        obeDebit,
        obeCredit,
        "Opening Balance Equity offset",
        asOfDate,
        sortIdx++,
      ),
    );
  }
}
